import {
  Box, Button, HStack, Icon, Menu, MenuButton, MenuDivider, MenuItem, MenuList,
  Portal, Spacer, VStack,
} from "@chakra-ui/react";
import {
  FiArrowDown, FiArrowUp, FiCalendar, FiCheck, FiChevronDown,
} from "react-icons/fi";
import {
  TbAlertTriangle, TbArrowsSort, TbCircleDashed, TbGripVertical,
} from "react-icons/tb";
import CDS from "design-system";
import type { SortMode } from "../types";

interface Props {
  sort: SortMode;
  onChange: (next: SortMode) => void;
}

const TRIGGER_LABEL: Record<SortMode, string> = {
  most_overdue: "Most overdue",
  least_complete: "Least complete",
  start_date_asc: "Start date",
  start_date_desc: "Start date",
  name_asc: "Name",
  name_desc: "Name",
  custom: "Custom",
};

const isStartDate = (s: SortMode) => s === "start_date_asc" || s === "start_date_desc";
const isName = (s: SortMode) => s === "name_asc" || s === "name_desc";

function SortMenuItem({
  icon, title, subtitle, active, indicator, onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  active: boolean;
  indicator?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <MenuItem onClick={onClick} py={2.5}>
      <HStack spacing={3} align="center" w="100%">
        <Box color="gray.700" display="flex" alignItems="center" justifyContent="center" w="22px" h="22px" flexShrink={0}>
          {icon}
        </Box>
        <VStack align="start" spacing={0} flex={1} minW={0}>
          <CDS.Text12 fontWeight="semibold" color="gray.900">{title}</CDS.Text12>
          <CDS.Text10 color="gray.500">{subtitle}</CDS.Text10>
        </VStack>
        <Spacer />
        {indicator}
        {active && !indicator && <Icon as={FiCheck} color="blue.500" boxSize={4} />}
      </HStack>
    </MenuItem>
  );
}

/**
 * Sort dropdown for the people axis. Lives next to the role filter.
 *
 * Start date and Name are single items that toggle asc/desc on click — the
 * current direction shows on the right with an arrow. Custom appears below a
 * divider since it represents the user's manual arrangement rather than an
 * automatic sort.
 */
export default function SortMenu({ sort, onChange }: Props) {
  // Toggling from asc→desc→asc. First click from any other sort goes to asc
  // (the default), so the arrow points up and the label reads "oldest first".
  const toggleStartDate = () => onChange(sort === "start_date_asc" ? "start_date_desc" : "start_date_asc");
  const toggleName = () => onChange(sort === "name_asc" ? "name_desc" : "name_asc");
  const startDir = sort === "start_date_asc" ? "asc" : "desc";
  const nameDir = sort === "name_desc" ? "desc" : "asc";

  return (
    <Menu placement="bottom-end">
      <MenuButton
        as={Button}
        size="sm"
        variant="outline"
        colorScheme="gray"
        leftIcon={<Icon as={TbArrowsSort} boxSize={4} />}
        rightIcon={<Icon as={FiChevronDown} boxSize={4} />}
        fontWeight="normal"
        h="28px"
        minH="28px"
      >
        {TRIGGER_LABEL[sort]}
      </MenuButton>
      <Portal>
        <MenuList minW="340px" py={2}>
          <CDS.Text10
            px={4}
            py={2}
            color="gray.500"
            letterSpacing="wide"
            fontWeight="semibold"
            textTransform="uppercase"
          >
            Sort people by
          </CDS.Text10>

          <SortMenuItem
            icon={<Icon as={FiCalendar} color="gray.700" boxSize={4} />}
            title="Start date"
            subtitle={startDir === "asc" ? "oldest first" : "latest first"}
            active={isStartDate(sort)}
            indicator={isStartDate(sort) ? (
              <HStack spacing={1} color="gray.600">
                <Icon as={startDir === "asc" ? FiArrowUp : FiArrowDown} boxSize={3.5} />
                <CDS.Text10>{startDir}</CDS.Text10>
              </HStack>
            ) : undefined}
            onClick={toggleStartDate}
          />

          <SortMenuItem
            icon={<CDS.Text11 fontWeight="bold" color="gray.700" letterSpacing="-0.05em">A-Z</CDS.Text11>}
            title="Name"
            subtitle={nameDir === "asc" ? "A-Z" : "Z-A"}
            active={isName(sort)}
            indicator={isName(sort) ? (
              <HStack spacing={1} color="gray.600">
                <Icon as={nameDir === "asc" ? FiArrowUp : FiArrowDown} boxSize={3.5} />
                <CDS.Text10>{nameDir}</CDS.Text10>
              </HStack>
            ) : undefined}
            onClick={toggleName}
          />

          <SortMenuItem
            icon={<Icon as={TbAlertTriangle} color="red.500" boxSize={5} />}
            title="Most overdue"
            subtitle="fires first"
            active={sort === "most_overdue"}
            onClick={() => onChange("most_overdue")}
          />

          <SortMenuItem
            icon={<Icon as={TbCircleDashed} color="gray.500" boxSize={5} />}
            title="Least complete"
            subtitle="furthest behind overall"
            active={sort === "least_complete"}
            onClick={() => onChange("least_complete")}
          />

          <MenuDivider />

          <SortMenuItem
            icon={<Icon as={TbGripVertical} color="gray.500" boxSize={5} />}
            title="Custom"
            subtitle="your manual order"
            active={sort === "custom"}
            onClick={() => onChange("custom")}
          />
        </MenuList>
      </Portal>
    </Menu>
  );
}
